/**
 * Comptes d'exploitation — qui peut se connecter, avec quels droits.
 *
 * Page réservée aux administrateurs (`operators:manage`). Elle remplace le
 * script en ligne de commande (`jobs/createOperator.ts`) pour tout ce qui se
 * fait au quotidien : ouvrir un compte à un nouvel agent du TROC, changer un
 * rôle, remettre un mot de passe, fermer un compte, déconnecter un poste laissé
 * ouvert. Le script reste pour le tout premier compte et le dépannage.
 *
 * Deux choix d'écran, volontaires :
 *  · un compte ne se SUPPRIME pas, il se ferme (`disabled`) : l'historique des
 *    actions garde un nom derrière chaque publication ;
 *  · le mot de passe est généré ici et montré UNE fois, à l'écran, pour être
 *    transmis à la personne ; le serveur n'en garde qu'une empreinte.
 */

import { useState } from 'react';

import { ApiError, MIN_PASSWORD_LENGTH, ROLES, ROLE_LABELS, api } from '../lib/api';
import type { OperatorAccount, Role } from '../lib/api';
import { formatDateTime } from '../lib/format';
import { useSession } from '../lib/session';
import { useApi } from '../lib/useApi';
import { toast } from '../components/Toast';
import { Async, Badge, Banner, Card } from '../components/ui';

/** Ce que chaque rôle permet, dit simplement — c'est ce qu'on lit avant de choisir. */
const ROLE_HELP: Record<Role, string> = {
  viewer: 'Regarde les chiffres et les plans. Ne modifie rien.',
  operator: 'Agent TROC : publie les départs, traite les signalements, génère les rapports.',
  admin: 'Tout cela, plus la gestion des comptes.',
};

/** Mot de passe lisible à l'oral : lettres et chiffres sans ambiguïté (pas de 0/O, 1/l). */
function generatePassword(length = 14): string {
  const alphabet = 'abcdefghjkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  const bytes = new Uint32Array(length);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => alphabet[b % alphabet.length]).join('');
}

/** Identifiant proposé depuis le nom : « Awa Ndiaye » → « awa.ndiaye ». */
function suggestUsername(displayName: string): string {
  return displayName
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '.')
    .replace(/^\.|\.$/g, '')
    .slice(0, 64);
}

export function AccountsPage(): React.ReactElement {
  const session = useSession();
  const [nonce, setNonce] = useState(0);
  const accounts = useApi((s) => api.operators(s), `operators|${nonce}`);
  const refresh = (): void => setNonce((n) => n + 1);

  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  /** Mot de passe à transmettre, affiché une seule fois après création ou remise à zéro. */
  const [reveal, setReveal] = useState<{ username: string; password: string } | null>(null);

  const run = async (key: string, action: () => Promise<void>, done?: string): Promise<void> => {
    setBusy(key);
    setError(null);
    try {
      await action();
      refresh();
      toast(done ?? 'Enregistré');
    } catch (cause) {
      setError(cause instanceof ApiError ? `${cause.userMessage}${cause.problem.detail ? ` : ${cause.problem.detail}` : ''}` : String(cause));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="page">
      {error ? (
        <Banner tone="bad" title="L'action n'a pas abouti">
          {error}
        </Banner>
      ) : null}

      {reveal ? (
        <Banner tone="info" title={`Mot de passe de ${reveal.username}, à transmettre maintenant`}>
          Il ne sera plus affiché ensuite. <code className="mono">{reveal.password}</code>{' '}
          <button type="button" className="btn btn-sm" onClick={() => void navigator.clipboard?.writeText(reveal.password)}>
            Copier
          </button>{' '}
          <button type="button" className="btn btn-sm" onClick={() => setReveal(null)}>
            J'ai transmis
          </button>
        </Banner>
      ) : null}

      <div className="grid grid-2">
        <NewAccountCard busy={busy} run={run} onCreated={(username, password) => setReveal({ username, password })} />
        <Card title="Les trois rôles" hint="Choisissez le plus petit rôle qui suffit : on l'augmente en un clic plus tard.">
          <ul className="roles-help">
            {ROLES.map((role) => (
              <li key={role}>
                <strong>{ROLE_LABELS[role]}</strong>
                <span>{ROLE_HELP[role]}</span>
              </li>
            ))}
          </ul>
        </Card>
      </div>

      <Card title="Comptes existants" hint="Les comptes fermés restent listés : leur nom reste attaché à ce qu'ils ont publié.">
        <Async state={accounts}>
          {(items) =>
            items.length === 0 ? (
              <div className="state state-empty">
                <span className="state-title">Aucun compte</span>
              </div>
            ) : (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Nom</th>
                      <th>Identifiant</th>
                      <th>Rôle</th>
                      <th>Dernière connexion</th>
                      <th>État</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((account) => (
                      <AccountRow
                        key={account.id}
                        account={account}
                        isSelf={session?.operator.id === account.id}
                        busy={busy}
                        run={run}
                        onPasswordReset={(password) => setReveal({ username: account.username, password })}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            )
          }
        </Async>
      </Card>
    </div>
  );
}

function NewAccountCard(props: {
  busy: string | null;
  run: (key: string, action: () => Promise<void>, done?: string) => Promise<void>;
  onCreated: (username: string, password: string) => void;
}): React.ReactElement {
  const { busy, run, onCreated } = props;
  const [displayName, setDisplayName] = useState('');
  const [username, setUsername] = useState('');
  const [usernameTouched, setUsernameTouched] = useState(false);
  const [role, setRole] = useState<Role>('operator');

  const effectiveUsername = usernameTouched ? username : suggestUsername(displayName);
  const canSubmit = displayName.trim().length >= 2 && effectiveUsername.length >= 3 && busy === null;

  return (
    <Card title="Ouvrir un compte" hint="Le mot de passe est créé pour vous et affiché une fois, à transmettre à la personne.">
      <div className="toolbar">
        <label className="field">
          Nom de la personne
          <input
            type="text"
            value={displayName}
            placeholder="Prénom Nom"
            maxLength={96}
            onChange={(e) => setDisplayName(e.target.value)}
          />
        </label>
        <label className="field">
          Identifiant de connexion
          <input
            type="text"
            value={effectiveUsername}
            placeholder="prenom.nom"
            maxLength={64}
            onChange={(e) => {
              setUsernameTouched(true);
              setUsername(e.target.value.toLowerCase());
            }}
          />
        </label>
        <label className="field">
          Rôle
          <select value={role} onChange={(e) => setRole(e.target.value as Role)}>
            {ROLES.map((r) => (
              <option key={r} value={r}>
                {ROLE_LABELS[r]}
              </option>
            ))}
          </select>
        </label>
        <button
          type="button"
          className="btn btn-primary"
          disabled={!canSubmit}
          onClick={() =>
            void run('create', async () => {
              const password = generatePassword();
              const created = await api.createOperator({ username: effectiveUsername, displayName: displayName.trim(), password, role });
              onCreated(created.username, password);
              setDisplayName('');
              setUsername('');
              setUsernameTouched(false);
              setRole('operator');
            }, 'Compte créé')
          }
        >
          {busy === 'create' ? 'Création…' : 'Créer le compte'}
        </button>
      </div>
      <p className="muted small" style={{ marginTop: 10 }}>
        {ROLE_HELP[role]} · Mots de passe de {MIN_PASSWORD_LENGTH} caractères au moins.
      </p>
    </Card>
  );
}

function AccountRow(props: {
  account: OperatorAccount;
  isSelf: boolean;
  busy: string | null;
  run: (key: string, action: () => Promise<void>, done?: string) => Promise<void>;
  onPasswordReset: (password: string) => void;
}): React.ReactElement {
  const { account, isSelf, busy, run, onPasswordReset } = props;
  const closed = account.disabledAt !== null;
  const key = `acc:${account.id}`;
  const working = busy === key;
  const [confirm, setConfirm] = useState<'close' | 'password' | null>(null);

  return (
    <tr>
      <td>
        <strong>{account.displayName}</strong>
        {isSelf ? (
          <>
            {' '}
            <Badge tone="info">vous</Badge>
          </>
        ) : null}
      </td>
      <td className="mono">{account.username}</td>
      <td>
        {closed ? (
          ROLE_LABELS[account.role]
        ) : (
          <select
            value={account.role}
            disabled={working || isSelf}
            title={isSelf ? 'Vous ne pouvez pas changer votre propre rôle.' : 'Changer le rôle'}
            onChange={(e) => void run(key, () => api.patchOperator(account.id, { role: e.target.value as Role }).then(() => undefined))}
          >
            {ROLES.map((r) => (
              <option key={r} value={r}>
                {ROLE_LABELS[r]}
              </option>
            ))}
          </select>
        )}
      </td>
      <td>{account.lastLoginAt ? formatDateTime(account.lastLoginAt) : <span className="muted">jamais</span>}</td>
      <td>{closed ? <Badge tone="bad">fermé le {formatDateTime(account.disabledAt)}</Badge> : <Badge tone="ok">actif</Badge>}</td>
      <td>
        {confirm === 'close' ? (
          <span className="acc-confirm">
            <span className="small">Fermer ce compte ? La personne ne pourra plus se connecter.</span>
            <button
              type="button"
              className="btn btn-sm btn-primary"
              disabled={working}
              onClick={() =>
                void run(key, async () => {
                  await api.patchOperator(account.id, { disabled: true });
                  setConfirm(null);
                }, 'Compte fermé')
              }
            >
              Oui, fermer
            </button>
            <button type="button" className="btn btn-sm" onClick={() => setConfirm(null)}>
              Non
            </button>
          </span>
        ) : confirm === 'password' ? (
          <span className="acc-confirm">
            <span className="small">Nouveau mot de passe ? L'ancien cessera de fonctionner et ses sessions seront fermées.</span>
            <button
              type="button"
              className="btn btn-sm btn-primary"
              disabled={working}
              onClick={() =>
                void run(key, async () => {
                  const password = generatePassword();
                  await api.patchOperator(account.id, { password });
                  onPasswordReset(password);
                  setConfirm(null);
                })
              }
            >
              Oui, remettre
            </button>
            <button type="button" className="btn btn-sm" onClick={() => setConfirm(null)}>
              Non
            </button>
          </span>
        ) : closed ? (
          <button aria-busy={working}
            type="button"
            className="btn btn-sm"
            disabled={working}
            onClick={() => void run(key, () => api.patchOperator(account.id, { disabled: false }).then(() => undefined))}
          >
            Rouvrir
          </button>
        ) : (
          <span className="acc-actions">
            <button type="button" className="btn btn-sm" disabled={working} onClick={() => setConfirm('password')}>
              Nouveau mot de passe
            </button>
            <button
              type="button"
              className="btn btn-sm"
              disabled={working}
              title="Ferme toutes les sessions ouvertes de ce compte, sur tous les postes."
              onClick={() => void run(key, () => api.revokeOperatorSessions(account.id).then(() => undefined), 'Déconnecté sur tous les postes')}
            >
              Déconnecter partout
            </button>
            {isSelf ? null : (
              <button type="button" className="btn btn-sm" disabled={working} onClick={() => setConfirm('close')}>
                Fermer le compte
              </button>
            )}
          </span>
        )}
      </td>
    </tr>
  );
}
